import { Button, Icon, Modal, Spinner } from '@toggle/design-system';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRows } from '~/components/chat';
import { useRefinePromptQuery } from '~/react-query-hooks/prompt-refinement/useRefinePromptQuery';
import {
  PromptRefinerTrackingArgs,
  usePosthogEvents,
} from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

import { PromptAngleCard } from './PromptAngleCard';
import styles from './PromptRefinementModal.module.scss';

export interface PromptRefinementModalProps {
  isOpen: boolean;
  question: string;
  tracking: PromptRefinerTrackingArgs;
  onClose: () => void;
  onSelectPrompt: (prompt: string) => void;
  onSelectPrompts: (prompts: string[]) => void;
  onUpdateQuestion: (question: string) => void;
  allowMultiSelect: boolean;
}

export const PromptRefinementModal = ({
  isOpen,
  question,
  tracking,
  onClose,
  onSelectPrompt,
  onSelectPrompts,
  onUpdateQuestion,
  allowMultiSelect,
}: PromptRefinementModalProps) => {
  const { t } = useTranslation('chat');
  const {
    trackPromptRefinerClarificationAdded,
    trackPromptRefinerSuggestionEdited,
  } = usePosthogEvents();
  const [editedQuestion, setEditedQuestion] = useState(question);
  const [selectedPromptIndices, setSelectedPromptIndices] = useState<
    Set<number>
  >(() => new Set());
  const [promptOverrides, setPromptOverrides] = useState<
    Record<number, string>
  >({});
  const clarificationInputRef = useRef<HTMLTextAreaElement>(null);
  const hasTrackedClarificationRef = useRef(false);
  const hasTrackedSuggestionEditRef = useRef(false);
  const { rows, updateRows } = useRows({
    value: editedQuestion,
    textAreaRef: clarificationInputRef,
    compactMode: true,
  });

  const {
    data,
    isFetching,
    isError,
    refetch: handleRetry,
  } = useRefinePromptQuery({ question, isEnabled: isOpen });

  const angles = data?.angles ?? [];
  const clarification = data?.clarification ?? null;
  const showClarification = !!clarification && !isFetching && !isError;

  useEffect(() => {
    setEditedQuestion(question);
  }, [question, isOpen]);

  useEffect(() => {
    if (isOpen) {
      hasTrackedClarificationRef.current = false;
      hasTrackedSuggestionEditRef.current = false;
      setSelectedPromptIndices(new Set());
      setPromptOverrides({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showClarification) {
      return;
    }

    const el = clarificationInputRef.current;

    if (!el) {
      return;
    }

    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [showClarification]);

  const hasPendingEdit = editedQuestion.trim() !== question.trim();

  const handleResetEdit = () => {
    setEditedQuestion(question);
    onClose();
  };

  const handleApplyEdit = () => {
    const trimmed = editedQuestion.trim();

    if (!trimmed || trimmed === question.trim()) {
      return;
    }

    if (!hasTrackedClarificationRef.current) {
      trackPromptRefinerClarificationAdded(tracking);
      hasTrackedClarificationRef.current = true;
    }

    onUpdateQuestion(trimmed);
  };

  const handleSuggestionEdited = () => {
    if (hasTrackedSuggestionEditRef.current) {
      return;
    }

    trackPromptRefinerSuggestionEdited(tracking);
    hasTrackedSuggestionEditRef.current = true;
  };

  const handleSelectionChange = (index: number, isSelected: boolean) => {
    setSelectedPromptIndices(prev => {
      const next = new Set(prev);

      if (isSelected) {
        next.add(index);
      } else {
        next.delete(index);
      }

      return next;
    });
  };

  const handlePromptChange = (index: number, prompt: string) => {
    setPromptOverrides(prev => ({
      ...prev,
      [index]: prompt,
    }));
  };

  const selectedPrompts = useMemo(
    () =>
      angles
        .map((angle, index) =>
          selectedPromptIndices.has(index)
            ? (promptOverrides[index] ?? angle.prompt)
            : null
        )
        .filter((prompt): prompt is string => !!prompt?.trim()),
    [angles, promptOverrides, selectedPromptIndices]
  );

  const hasSelectedPrompts = selectedPrompts.length > 0;

  const handleSendSelectedPrompts = () => {
    if (!hasSelectedPrompts) {
      return;
    }

    onSelectPrompts(selectedPrompts);
  };

  const modalTitle = showClarification ? (
    <span className={styles.clarificationTitle}>
      <Icon
        iconName="ExclamationCircle"
        fillColor="var(--icon-primary)"
        size={20}
      />
      {t('chat:promptRefinement.clarificationTitle')}
    </span>
  ) : (
    t('chat:promptRefinement.title')
  );
  const modalDescription = showClarification
    ? clarification
    : t('chat:promptRefinement.description');

  return (
    <Modal
      isOpen={isOpen}
      className={styles.centeredModal}
      title={modalTitle}
      description={modalDescription}
      onClose={onClose}
      data-testid="prompt-refinement-modal"
      disableEnterClose
      primaryBtnProps={
        allowMultiSelect && !isFetching && !isError
          ? {
              label: t('chat:promptRefinement.sendSelected'),
              onClick: handleSendSelectedPrompts,
              disabled: !hasSelectedPrompts,
            }
          : undefined
      }
      secondaryBtnProps={
        isError
          ? {
              type: 'button',
              label: t('chat:promptRefinement.retry'),
              onClick: () => handleRetry(),
            }
          : undefined
      }
    >
      <div className={styles.content}>
        {isFetching && (
          <div className={styles.statusMessage}>
            <Spinner size="small" useAccentColor />
            <p>{t('chat:promptRefinement.loading')}</p>
          </div>
        )}

        {isError && (
          <div className={styles.statusMessage} role="alert">
            <p>{t('chat:promptRefinement.error')}</p>
          </div>
        )}

        {showClarification && (
          <div className={styles.clarification}>
            <textarea
              ref={clarificationInputRef}
              className={styles.editInput}
              value={editedQuestion}
              onChange={e => {
                updateRows(e);
                setEditedQuestion(e.target.value);
              }}
              rows={rows}
              aria-label={t('chat:promptRefinement.clarificationTitle')}
              data-testid="prompt-refinement-clarification-input"
            />
            <div className={styles.editActions}>
              <Button
                type="button"
                variant="tertiary"
                size="small"
                label={t('chat:promptRefinement.cancel')}
                onClick={handleResetEdit}
              />
              <Button
                type="button"
                variant="secondary"
                size="small"
                label={t('chat:promptRefinement.update')}
                onClick={handleApplyEdit}
                disabled={!hasPendingEdit || !editedQuestion.trim()}
              />
            </div>
          </div>
        )}

        {!isFetching &&
          !isError &&
          angles.map((angle, index) => (
            <PromptAngleCard
              key={`${index}-${angle.lens}-${angle.label}`}
              angle={angle}
              isOpen={isOpen}
              onSelectPrompt={onSelectPrompt}
              onSuggestionEdited={handleSuggestionEdited}
              isSelected={selectedPromptIndices.has(index)}
              onSelectionChange={isSelected =>
                handleSelectionChange(index, isSelected)
              }
              onPromptChange={prompt => handlePromptChange(index, prompt)}
              showSelection={allowMultiSelect}
            />
          ))}
      </div>
    </Modal>
  );
};
